import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { loadHookEventsFrontendData } from '../../../Util/HookEventsFrontendData';

const HookSessionFeeSelect = (props) => {
	const { t } = useTranslation();
	const { displayIndex } = props;
	const [sessionFeeOptions, setSessionFeeOptions] = useState([]);
	const [selectedFees, setSelectedFees] = useState([]);

	// State to track session data changes
	const [sessionData, setSessionData] = useState({
		loaded: false,
		reg_multiple_participants: true
	});

	// Load session data from HookEvents AJAX endpoint
	const loadSessionDataFromAPI = async () => {
		try {
			// Get funnel_page_id from props or DOM
			const funnelPageId = props.funnel_page_id || document.querySelector('[data-funnel-page-id]')?.getAttribute('data-funnel-page-id');
			const lang = props.lang || 'zh-TW';
			if (!funnelPageId) {
				// Set fallback data when no funnel_page_id
				const fallbackData = {
					loaded: true,
					reg_multiple_participants: true
				};
				setSessionData(fallbackData);
				return;
			}
			
			// Load session data from HookEvents API
			try {
				const apiData = await loadHookEventsFrontendData(funnelPageId, lang);
				const newSessionData = {
					loaded: true,
					reg_multiple_participants: apiData.reg_multiple_participants !== undefined ? apiData.reg_multiple_participants : true
				};
				setSessionData(newSessionData);
			} catch (hookEventsError) {
				// HookEvents API failed, set fallback data
				throw new Error('HookEvents API failed');
			}
		} catch (error) {
			// Set fallback data on API failure
			const fallbackData = {
				loaded: true,
				reg_multiple_participants: true
			};
			setSessionData(fallbackData);
		}
	};

	// Load session data from API on component mount
	useEffect(() => {
		let didLoad = false;
		const tryLoad = () => {
			if (didLoad) return;
			didLoad = true;
			loadSessionDataFromAPI();
		};

		if (window.turnstileToken) {
			tryLoad();
		} else {
			const onReady = () => tryLoad();
			document.addEventListener('turnstileReady', onReady, { once: true });
			setTimeout(tryLoad, 2000);
			return () => {
				document.removeEventListener('turnstileReady', onReady);
			};
		}
	}, []);

    // Get session fees with quantities from SessionFeeQty components
    const getSessionFeesWithQuantities = () => {
        // Look for hidden input fields with the new structure: page_user_hook_session[hook_session_fees][sessionFeeId][qty]
        const sessionFeeInputs = document.querySelectorAll('input.qty-hidden[name*="page_user_hook_session[hook_session_fees]"][name*="[qty]"]');
        const feesWithQuantities = [];

        sessionFeeInputs.forEach((input) => {
            // Match the new format: page_user_hook_session[hook_session_fees][68d3f1477726c805d3017ca1][qty]
            const sessionFeeId = input.name.match(/\[([a-f0-9]{24})\]\[qty\]/)?.[1];
            
            if (!sessionFeeId) {
                return;
            }

            // Get quantity and only process if qty > 0
            const qty = parseInt(input.value, 10);
            if (qty <= 0) {
                return;
            }

            // Find the corresponding session fee name from the DOM
            // Look for the ticket row container
            const ticketRow = input.closest('.ticket-row');
            if (!ticketRow) {
                return;
            }

            // Find the session name display element
            const nameElement = ticketRow.querySelector('.ticket_name.session-name-display span');
            if (!nameElement) {
                return;
            }

            // Use innerHTML to get raw HTML content
            const rawHtml = nameElement.innerHTML || '';
            
            // Create a temporary div to decode HTML entities properly
            const tempDiv = document.createElement('div');
            tempDiv.innerHTML = rawHtml;
            const decodedText = tempDiv.textContent || tempDiv.innerText || '';
            
            const normalized = decodedText
                .replace(/<br\s*\/?>/gi, ' ') // <br> to space
                .replace(/\r?\n/g, ' ')       // newline to space
                .replace(/\s+/g, ' ')          // collapse spaces
                .trim();

            if (normalized) {
                feesWithQuantities.push({
                    id: sessionFeeId,
                    name: normalized,
                    qty: qty
                });
            }
        });

        return feesWithQuantities;
    };

    // Update session fee options when component mounts or DOM changes
    useEffect(() => {
        let debounceTimer;
        let isInitialized = false;
        
        const updateOptions = () => {
            clearTimeout(debounceTimer);
            debounceTimer = setTimeout(() => {
                const fees = getSessionFeesWithQuantities();
                setSessionFeeOptions(fees);
                
                // Only pre-select fees on initial load, not on subsequent updates
                if (!isInitialized) {
                    const preSelectedFees = [];
                    let currentIndex = 0;
                    fees.forEach(fee => {
                        for (let i = 0; i < fee.qty; i++) {
                            preSelectedFees[currentIndex] = fee.id;
                            currentIndex++;
                        }
                    });
                    setSelectedFees(preSelectedFees);
                    isInitialized = true;
                }
            }, 100);
        };

        // Initial update and slight delay to wait other app mount
        updateOptions();
        const initialTimer = setTimeout(updateOptions, 200);

        // Listen for changes in session fee quantity selections
        const buildSelectedFees = (fees, previousSelectedFees = []) => {
            const nextSelectedFees = [];
            let currentIndex = 0;
            fees.forEach(fee => {
                for (let i = 0; i < fee.qty; i++) {
                    nextSelectedFees[currentIndex] = previousSelectedFees[currentIndex] || fee.id;
                    currentIndex++;
                }
            });

            return nextSelectedFees;
        };

        const handleFeeQtyChanged = () => {
            clearTimeout(debounceTimer);
            debounceTimer = setTimeout(() => {
                const fees = getSessionFeesWithQuantities();
                setSessionFeeOptions(fees);
                setSelectedFees(prevSelectedFees => buildSelectedFees(fees, prevSelectedFees));
            }, 100);
        };
        document.addEventListener('feeQtyChanged', handleFeeQtyChanged);

        // Listen for overall form ready signal
        const handleReactFormLoaded = () => {
            clearTimeout(debounceTimer);
            debounceTimer = setTimeout(() => {
                const fees = getSessionFeesWithQuantities();
                setSessionFeeOptions(fees);
                setSelectedFees(prevSelectedFees => buildSelectedFees(fees, prevSelectedFees));
            }, 100);
        };
        document.addEventListener('reactFormLoaded', handleReactFormLoaded);

        const observerTarget = document.querySelector('.ticket-display') || document.querySelector('#hook-sessions');
        const observer = observerTarget ? new MutationObserver(() => {
            clearTimeout(debounceTimer);
            debounceTimer = setTimeout(() => {
                const fees = getSessionFeesWithQuantities();
                setSessionFeeOptions(fees);
            }, 100);
        }) : null;
        if (observer && observerTarget) {
            observer.observe(observerTarget, { childList: true, subtree: true });
        }

        // Cleanup
        return () => {
            clearTimeout(initialTimer);
            clearTimeout(debounceTimer);
            document.removeEventListener('feeQtyChanged', handleFeeQtyChanged);
            document.removeEventListener('reactFormLoaded', handleReactFormLoaded);
            if (observer) {
                observer.disconnect();
            }
        };
    }, []);

    // Check total quantity first
    const getTotalQuantity = () => {
        let totalQty = 0;
        sessionFeeOptions.forEach(fee => {
            totalQty += fee.qty;
        });
        return totalQty;
    };

    // Create options for select boxes based on quantities
    const createSelectOptions = () => {
        const options = [];
        sessionFeeOptions.forEach(fee => {
            options.push({
                id: fee.id,
                name: fee.name,
                qty: fee.qty
            });
        });
        return options;
    };

    const selectOptions = createSelectOptions();

    // Handle fee selection change
    const handleFeeSelectionChange = (index, feeId) => {
        setSelectedFees(prev => {
            const newSelected = [...prev];
            newSelected[index] = feeId;
            return newSelected;
        });
    };

	// If reg_multiple_participants is false, don't show ticket type selection
	if (sessionData.loaded && !sessionData.reg_multiple_participants) {
		return null;
	}

    // Don't display if no session fee options available
    if (sessionFeeOptions.length === 0) {
        return null;
    }

    // If displayIndex is provided, only show the specific selection for that index
    if (displayIndex !== undefined && displayIndex !== null) {
        const totalQty = getTotalQuantity();
        if (displayIndex >= totalQty) {
            return null; // Don't show if index is out of range
        }
        
        return (
            <div className="session-fee-select w-100">
                <div className="session-fee-item mb-2">
                    <div className="form-control border-0 p-0 bg-transparent">
                        <div className="mb-3">
                            <label className="form-label me-3 mb-1 fw-bolder fs-5" htmlFor={`fee-select-${displayIndex}`}>
                                {t('票種')}
                            </label>
                            <select
                                className="form-select form-select-sm flex-grow-1"
                                id={`fee-select-${displayIndex}`}
                                name={`hook_session_fee_selection[${displayIndex}]`}
                                value={selectedFees[displayIndex] || ''}
                                onChange={(e) => handleFeeSelectionChange(displayIndex, e.target.value)}
                            >
                                {selectOptions.map((option) => (
                                    <option key={option.id} value={option.id}>
                                        {option.name}
                                    </option>
                                ))}
                            </select>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    // Don't display if total qty <= 1 for main display
    const totalQty = getTotalQuantity();
    if (totalQty < 1) {
        return null;
    }

    return (
        <div className="session-fee-select">
            <div className="session-fee-item mb-2">
                <div className="form-control border-0 p-0">
                    <div className="gap-2 pb-2 text-gray-700 border-bottom pb-2 border-dashed mb-3">
                        <svg version="1.1" id="Capa_1" xmlns="http://www.w3.org/2000/svg"
                            xmlnsXlink="http://www.w3.org/1999/xlink" x="0px" y="0px" viewBox="0 0 512 512"
                            style={{enableBackground: 'new 0 0 512 512'}} xmlSpace="preserve" width="20"
                            height="20">
                            <g>
                                <g>
                                    <path d="M484.719,168.958L431.025,53.042L180.782,168.958H0v290h330.538l2.101-12.517c3.646-21.719,22.302-37.483,44.361-37.483
c22.059,0,40.716,15.764,44.361,37.483l2.101,12.517H512v-290H484.719z M140,398.958H60v-30h80V398.958z M210,318.958H60v-30h150
V318.958z M240,258.958H60v-30h180V258.958z M423.462,168.958l-2.101,12.517c-3.646,21.719-22.302,37.483-44.361,37.483
c-22.059,0-40.716-15.764-44.361-37.483l-2.101-12.517h-78.379l164.255-76.084l35.243,76.084H423.462z"
                                        fill="currentcolor"></path>
                                </g>
                            </g>
                        </svg>
                        <span className="my-0 fs-5 fw-bolder">{t('票種')}</span>
                    </div>
                    
                    {Array.from({ length: getTotalQuantity() }, (_, index) => (
                        <div key={index} className="mb-3 d-flex align-items-center">
                            <label className="form-label me-3 mb-0 fw-bolder fs-5" htmlFor={`fee-select-${index}`}>
                                {t('票種')} {index + 1}
                            </label>
                            <select
                                className="form-select form-select-sm"
                                id={`fee-select-${index}`}
                                name={`hook_session_fee_selection[${index}]`}
                                value={selectedFees[index] || ''}
                                onChange={(e) => handleFeeSelectionChange(index, e.target.value)}
                            >
                                {selectOptions.map((option) => (
                                    <option key={option.id} value={option.id}>
                                        {option.name}
                                    </option>
                                ))}
                            </select>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
};

export default HookSessionFeeSelect;